/**
 * Layout Component
 * Main application layout with navigation
 */

import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { showsVesselBar } from "../navigation/vessel-bar";
import OfflineBar from "../offline/OfflineBar";
import UpdatePrompt from "../offline/UpdatePrompt";
import QueueBar from "../offline/QueueBar";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/auth-store";
import { getContactSubmissions } from "../services/api";
import LanguageSwitcher from "./LanguageSwitcher";
import MaintenanceBadge from "../logbook/MaintenanceBadge";
import VesselSelector from "./VesselSelector";

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const vesselBar = showsVesselBar(location.pathname);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileMenuOpen && !profileMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (profileMenuOpen) profileButtonRef.current?.focus();
      else mobileButtonRef.current?.focus();
      setProfileMenuOpen(false);
      setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen, profileMenuOpen]);
  const layoutRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const updateHeaderHeight = () => {
      const height = header.getBoundingClientRect().height;
      const menuHeight = header.querySelector<HTMLElement>("#main-mobile-navigation")?.getBoundingClientRect().height ?? 0;
      layoutRef.current?.style.setProperty("--app-header-height", `${Math.ceil(height)}px`);
      layoutRef.current?.style.setProperty("--app-toolbar-height", `${Math.ceil(height - menuHeight)}px`);
    };
    updateHeaderHeight();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeaderHeight);
      return () => window.removeEventListener("resize", updateHeaderHeight);
    }
    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const handleLogout = () => {
    // Clear all cached data to ensure fresh data on next login
    queryClient.clear();
    logout();
    navigate("/login");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [profileMenuOpen]);

  const isAdmin = user?.role === "admin";
  const isCrew = user?.role === "crew";
  const isAdminPage = location.pathname.startsWith("/admin");

  const { data: newContactCount } = useQuery({
    queryKey: ["contact-submissions-count"],
    queryFn: () => getContactSubmissions("new").then((r) => r.data?.length ?? 0),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Check if we're in a non-production environment
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const isDevEnvironment =
    environment === "development" || environment === "sandbox";
  const environmentLabel =
    environment === "sandbox"
      ? "SANDBOX"
      : environment === "development"
        ? "DEV"
        : null;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-11 items-center px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? isDevEnvironment
          ? "bg-red-200 text-red-900"
          : "bg-sea-100 text-sea-900"
        : isDevEnvironment
          ? "text-red-800 hover:bg-red-100 hover:text-red-900"
          : "text-sea-700 hover:bg-sea-50 hover:text-sea-900"
    }`;

  const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `block px-3 py-2 rounded-md text-base font-medium ${
      isActive
        ? isDevEnvironment
          ? "bg-red-200 text-red-900"
          : "bg-sea-100 text-sea-900"
        : isDevEnvironment
          ? "text-red-800 hover:bg-red-100 hover:text-red-900"
          : "text-sea-700 hover:bg-sea-50 hover:text-sea-900"
    }`;

  return (
    <div ref={layoutRef} className="min-h-screen bg-gray-50 print:bg-white">
      {/* Environment Banner - Dev/Sandbox only */}
      {isDevEnvironment && environmentLabel && (
        <div className="bg-red-600 text-white text-center text-xs font-bold py-1 print:hidden">
          {environmentLabel} ENVIRONMENT - Not Production
        </div>
      )}
      {/* Header - Sticky */}
      <header
        ref={headerRef}
        className={`sticky top-0 z-50 shadow-md border-b print:hidden ${
          isDevEnvironment
            ? "bg-red-50 border-red-200"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <a href="/dashboard" className="flex items-center gap-2">
                  <img
                    src="/images/Logo_Positiv_VoS.png"
                    alt="Vision of Stockholm"
                    className="h-8"
                  />
                </a>
              </div>

              {/* Navigation */}
              <nav className="hidden lg:block ml-4 xl:ml-10">
                <div className="flex items-baseline space-x-1 xl:space-x-4">
                  {!isCrew && (
                    <>
                  <NavLink to="/dashboard" className={navLinkClass}>
                    {t("nav.dashboard")}
                  </NavLink>
                  <NavLink to="/book" className={navLinkClass}>
                    {t("nav.newBooking")}
                  </NavLink>
                  <NavLink to="/trips" className={navLinkClass}>
                    {t("nav.trips")}
                  </NavLink>
                  <NavLink to="/calendar" className={navLinkClass}>
                    {t("nav.calendar")}
                  </NavLink>
                    </>
                  )}
                  {(isAdmin || isCrew) && (
                    <NavLink to="/maintenance" className={navLinkClass}>
                      {t("maintenance.title")}<MaintenanceBadge />
                    </NavLink>
                  )}
                  {(isAdmin || isCrew) && (
                    <NavLink to="/logbook" className={navLinkClass}>
                      {t("nav.logbook")}
                    </NavLink>
                  )}
                  {isAdmin && (
                    <NavLink to="/admin/contact" className={navLinkClass}>
                      <span className="relative inline-flex items-center">
                        {t("nav.inbox")}
                        {!!newContactCount && newContactCount > 0 && (
                          <span className="absolute -top-2 -right-5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                            {newContactCount > 99 ? "99+" : newContactCount}
                          </span>
                        )}
                      </span>
                    </NavLink>
                  )}
                </div>
              </nav>
            </div>

            {/* Right side menu items */}
            <div className="flex items-center space-x-2">
              <div className="hidden lg:block mr-2">
                <LanguageSwitcher />
              </div>

              {/* Help icon */}
              <NavLink
                to="/help"
                className="touch-target p-2 rounded-md text-sea-700 hover:bg-sea-50 hover:text-sea-900 transition-colors hidden lg:inline-flex items-center justify-center"
                title={t("nav.help")}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </NavLink>

              {/* Admin settings icon */}
              {isAdmin && (
                <NavLink
                  to="/settings"
                  title={t("nav.adminSettings")}
                  aria-label={t("nav.adminSettings")}
                  className={() =>
                    `touch-target hidden lg:inline-flex items-center justify-center rounded-md p-2 transition-colors ${
                      isAdminPage || location.pathname === "/settings"
                        ? isDevEnvironment
                          ? "bg-red-200 text-red-900"
                          : "bg-sea-100 text-sea-900"
                        : isDevEnvironment
                          ? "text-red-800 hover:bg-red-100"
                          : "text-sea-700 hover:bg-sea-50"
                    }`
                  }
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </NavLink>
              )}

              {/* Profile menu */}
              <div className="relative hidden lg:block" ref={profileMenuRef}>
                <button
                  ref={profileButtonRef}
                  aria-expanded={profileMenuOpen}
                  aria-controls={profileMenuOpen ? "profile-navigation" : undefined}
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="touch-target inline-flex items-center justify-center p-2 rounded-md text-sea-700 hover:bg-sea-50 hover:text-sea-900 transition-colors"
                  title={t("nav.profile")}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </button>

                {/* Profile Dropdown Menu */}
                {profileMenuOpen && (
                  <div id="profile-navigation" className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.name || user?.email}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 capitalize">
                          {user?.role}
                        </p>
                        {user?.partnerName && (
                          <p className="text-xs text-sea-600 mt-1">
                            {user.partnerName}
                          </p>
                        )}
                      </div>
                      {!isAdmin && user?.partnerId && (
                        <NavLink
                          to="/agency"
                          onClick={() => setProfileMenuOpen(false)}
                          className="block min-h-11 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-sea-50 hover:text-sea-800 transition-colors"
                        >
                          {t("nav.myAgency")}
                        </NavLink>
                      )}
                      <button
                        onClick={() => {
                          setProfileMenuOpen(false);
                          handleLogout();
                        }}
                        className="block min-h-11 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-sea-50 hover:text-sea-800 transition-colors"
                      >
                        {t("nav.logout")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                ref={mobileButtonRef}
                aria-expanded={mobileMenuOpen}
                aria-controls={mobileMenuOpen ? "main-mobile-navigation" : undefined}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="touch-target lg:hidden inline-flex items-center justify-center p-2 rounded-md text-sea-600 hover:text-sea-900 hover:bg-sea-50"
              >
                <span className="sr-only">{t("nav.openMainMenu")}</span>
                {mobileMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {(isAdmin || isCrew) && vesselBar && <div className="flex items-center justify-end gap-2 h-10 lg:h-12 border-t border-gray-200 px-4 text-sm sm:px-6 lg:px-8"><span className="text-gray-600">{t("logbook.vessel")}:</span><VesselSelector /></div>}

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div id="main-mobile-navigation" className="lg:hidden border-t border-gray-200 max-h-[calc(100dvh-var(--app-toolbar-height,8rem))] overflow-y-auto">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {!isCrew && (
                <>
              <NavLink
                to="/dashboard"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.dashboard")}
              </NavLink>
              <NavLink
                to="/book"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.newBooking")}
              </NavLink>
              <NavLink
                to="/trips"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.trips")}
              </NavLink>
              <NavLink
                to="/calendar"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.calendar")}
              </NavLink>
                </>
              )}
              {(isAdmin || isCrew) && (
                <NavLink to="/maintenance" className={mobileNavLinkClass} onClick={() => setMobileMenuOpen(false)}>
                  {t("maintenance.title")}<MaintenanceBadge />
                </NavLink>
              )}
              {(isAdmin || isCrew) && (
                <NavLink
                  to="/logbook"
                  className={mobileNavLinkClass}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("nav.logbook")}
                </NavLink>
              )}
              <NavLink
                to="/help"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.help")}
              </NavLink>
              {isAdmin && (
                <NavLink
                  to="/admin/contact"
                  className={mobileNavLinkClass}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="relative inline-flex items-center">
                    {t("nav.inbox")}
                    {!!newContactCount && newContactCount > 0 && (
                      <span className="absolute -top-2 -right-5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                        {newContactCount > 99 ? "99+" : newContactCount}
                      </span>
                    )}
                  </span>
                </NavLink>
              )}

              {isAdmin && (
                <NavLink to="/settings" className={mobileNavLinkClass} onClick={() => setMobileMenuOpen(false)}>
                  {t("nav.adminSettings")}
                </NavLink>
              )}

              <div className="border-t border-gray-200 my-2"></div>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t("nav.profile")}
              </div>
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || user?.email}
                </p>
                <p className="text-xs text-gray-500 mt-1 capitalize">
                  {user?.role}
                </p>
                {user?.partnerName && (
                  <p className="text-xs text-sea-600 mt-1">
                    {user.partnerName}
                  </p>
                )}
              </div>
              {!isAdmin && user?.partnerId && (
                <NavLink
                  to="/agency"
                  className={mobileNavLinkClass}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My agency
                </NavLink>
              )}
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-sea-600 hover:text-sea-900 hover:bg-sea-50"
              >
                {t("nav.logout")}
              </button>
            </div>
          </div>
        )}
      </header>

      <OfflineBar />
      <QueueBar />
      <UpdatePrompt />

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:max-w-none print:p-0">
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-gray-500 text-sm">
            {t("common.footer")}
          </p>
          <p className="text-center text-gray-400 text-xs mt-1">
            {t("common.version", { version: __APP_VERSION__ })}
          </p>
        </div>
      </footer>
    </div>
  );
}
